import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Tabs, Tab, Chip, Alert,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import GroupsIcon from '@mui/icons-material/Groups';
import StatCard from '../../components/common/StatCard';
import DataTable from '../../components/common/DataTable';
import { useAuth } from '../../context/AuthContext';
import db from '../../data/demoDatabase';

const PRIMARY = '#1B365D';

// Mask the user's name to show only last name (last word)
function lastNameOf(fullName) {
  if (!fullName) return '—';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  // For Malay names like "bin Ibrahim" / "binti Aisha", grab the last 2 parts
  const last = parts[parts.length - 1];
  const secondLast = parts[parts.length - 2];
  if (['bin', 'binti', 'a/l', 'a/p'].includes(secondLast.toLowerCase())) {
    return `${secondLast} ${last}`;
  }
  return last;
}

// eKYC completion check: a user is "eKYC complete" when they have all the
// core KYC fields filled in (name, phone, email, IC number)
function isEkycComplete(u) {
  if (!u) return false;
  return Boolean(u.name && u.phone && u.email && u.icNumber);
}

const columns = [
  {
    field: 'id', headerName: 'User ID', width: 120,
    renderCell: (row) => (
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: PRIMARY }}>
        {row.id}
      </Typography>
    ),
  },
  {
    field: 'lastName', headerName: 'Last Name',
    renderCell: (row) => (
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.lastName}</Typography>
    ),
  },
  {
    field: 'level', headerName: 'Level', width: 100,
    renderCell: (row) => (
      <Chip
        label={`L${row.level}`}
        size="small"
        sx={{
          fontWeight: 700,
          backgroundColor: row.level === 1 ? '#E3F2FD' : row.level === 2 ? '#F3E5F5' : '#FFF8E1',
          color: row.level === 1 ? '#1565C0' : row.level === 2 ? '#7B1FA2' : '#F57F17',
        }}
      />
    ),
  },
  {
    field: 'registrationStatus', headerName: 'Registration Status', width: 200,
    renderCell: (row) => (
      <Chip
        label={row.ekycComplete ? 'eKYC Completed' : 'Incomplete Registration'}
        size="small"
        sx={{
          fontWeight: 600,
          backgroundColor: row.ekycComplete ? '#E8F5E9' : '#FFF8E1',
          color: row.ekycComplete ? '#2E7D32' : '#F57F17',
        }}
      />
    ),
  },
  { field: 'joinDate', headerName: 'Joined Date', width: 130 },
];

export default function ReferralHistory() {
  const [tab, setTab] = useState(0);
  const { user } = useAuth();
  const [refreshKey] = useState(0);

  // Build the 3-level downline tree from the live db
  const tree = useMemo(() => {
    if (!user?.id) return { level1: [], level2: [], level3: [] };
    return db.users.getDownlineTree(user.id, 3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, refreshKey]);

  // Flatten into rows with level metadata
  const allRows = useMemo(() => {
    const rows = [];
    [1, 2, 3].forEach((lvl) => {
      const list = tree[`level${lvl}`] || [];
      list.forEach((u) => {
        rows.push({
          id: u.id,
          name: u.name,
          lastName: lastNameOf(u.name),
          level: lvl,
          ekycComplete: isEkycComplete(u),
          joinDate: u.joinDate || '—',
        });
      });
    });
    return rows;
  }, [tree]);

  // Stats
  const stats = useMemo(() => {
    const total = allRows.length;
    const level1 = allRows.filter((r) => r.level === 1).length;
    const level2 = allRows.filter((r) => r.level === 2).length;
    const level3 = allRows.filter((r) => r.level === 3).length;
    const ekycCompleted = allRows.filter((r) => r.ekycComplete).length;
    const incomplete = total - ekycCompleted;
    return { total, level1, level2, level3, ekycCompleted, incomplete };
  }, [allRows]);

  // Tab filter
  const filteredRows = useMemo(() => {
    if (tab === 0) return allRows;
    if (tab === 1) return allRows.filter((r) => r.level === 1);
    if (tab === 2) return allRows.filter((r) => r.level === 2);
    if (tab === 3) return allRows.filter((r) => r.level === 3);
    return allRows;
  }, [allRows, tab]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY, mb: 1 }}>My Referrals</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        3-level downline view — names are masked for privacy
      </Typography>

      {/* Stats */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Total Downline" value={stats.total} subtitle="all 3 levels"
            icon={GroupsIcon} color={PRIMARY} />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Level 1" value={stats.level1} subtitle="direct referrals"
            icon={PersonAddIcon} color="#1565C0" />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Level 2" value={stats.level2} subtitle="2nd-tier downline"
            icon={PersonAddIcon} color="#7B1FA2" />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Level 3" value={stats.level3} subtitle="3rd-tier downline"
            icon={PersonAddIcon} color="#F57F17" />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <StatCard title="eKYC Completed" value={stats.ekycCompleted}
            subtitle={stats.incomplete > 0 ? `${stats.incomplete} pending` : 'all verified'}
            icon={CheckCircleIcon} color="#00B894" />
        </Grid>
      </Grid>

      {/* Tabs by level */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: '1px solid #E0E0E0', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
          <Tab label={`All (${stats.total})`} />
          <Tab label={`Level 1 (${stats.level1})`} />
          <Tab label={`Level 2 (${stats.level2})`} />
          <Tab label={`Level 3 (${stats.level3})`} />
        </Tabs>
      </Card>

      {/* Table */}
      {allRows.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No referrals yet. Share your referral link to start building your downline and earn tokens.
        </Alert>
      ) : (
        <DataTable
          columns={columns}
          rows={filteredRows}
          title="Downline Members"
          searchable
          pageSize={15}
        />
      )}
    </Box>
  );
}
